import { useEffect, useRef } from 'react'
import { hexToRgb } from '../utils/color'

const CANVAS_SCALE = 16
const MIN_CELL_FOR_CODE = 16

export default function PatternViewer({ pattern, brand, showGrid = true, showColorCodes = false, canvasRef: externalCanvasRef }) {
  const internalCanvasRef = useRef(null)
  const canvasRef = externalCanvasRef || internalCanvasRef

  useEffect(() => {
    if (!pattern || !canvasRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const { grid, width, height } = pattern
    canvas.width = width * CANVAS_SCALE
    canvas.height = height * CANVAS_SCALE

    const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))
    const nameMap = new Map(brand.colors.map((c) => [c.id, c.name]))

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        ctx.fillStyle = colorMap.get(grid[y][x]) || '#000'
        ctx.fillRect(x * CANVAS_SCALE, y * CANVAS_SCALE, CANVAS_SCALE, CANVAS_SCALE)
      }
    }

    if (showGrid) {
      const group = 5
      // 细网格
      ctx.strokeStyle = 'rgba(255,255,255,0.25)'
      ctx.lineWidth = 1
      for (let x = 0; x <= width; x++) {
        ctx.beginPath()
        ctx.moveTo(x * CANVAS_SCALE, 0)
        ctx.lineTo(x * CANVAS_SCALE, height * CANVAS_SCALE)
        ctx.stroke()
      }
      for (let y = 0; y <= height; y++) {
        ctx.beginPath()
        ctx.moveTo(0, y * CANVAS_SCALE)
        ctx.lineTo(width * CANVAS_SCALE, y * CANVAS_SCALE)
        ctx.stroke()
      }
      // 每 5×5 一组粗线
      ctx.strokeStyle = 'rgba(255,255,255,0.7)'
      ctx.lineWidth = 2
      for (let x = 0; x <= width; x += group) {
        ctx.beginPath()
        ctx.moveTo(x * CANVAS_SCALE, 0)
        ctx.lineTo(x * CANVAS_SCALE, height * CANVAS_SCALE)
        ctx.stroke()
      }
      for (let y = 0; y <= height; y += group) {
        ctx.beginPath()
        ctx.moveTo(0, y * CANVAS_SCALE)
        ctx.lineTo(width * CANVAS_SCALE, y * CANVAS_SCALE)
        ctx.stroke()
      }
    }

    if (showColorCodes && CANVAS_SCALE >= MIN_CELL_FOR_CODE) {
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = `${Math.max(8, CANVAS_SCALE * 0.45)}px sans-serif`
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const id = grid[y][x]
          const hex = colorMap.get(id) || '#000'
          const code = nameMap.get(id) || ''
          const rgb = hexToRgb(hex)
          const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
          ctx.fillStyle = luminance > 0.5 ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)'
          ctx.fillText(code, x * CANVAS_SCALE + CANVAS_SCALE / 2, y * CANVAS_SCALE + CANVAS_SCALE / 2)
        }
      }
    }
  }, [pattern, brand, showGrid, showColorCodes])

  return (
    <div className="space-y-4">
      <div className="overflow-auto rounded-2xl border border-cream-200 bg-cream-100 p-2">
        <canvas
          ref={canvasRef}
          className="mx-auto block max-w-full h-auto rounded-xl shadow-sm"
          style={{ imageRendering: 'pixelated' }}
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-cocoa-light">色号图例 & 用量</h4>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {[...pattern.legend]
            .sort((a, b) => b.count - a.count)
            .map((item) => {
              const percent = pattern.total > 0 ? ((item.count / pattern.total) * 100).toFixed(1) : '0.0'
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-2xl border border-lavender/30 bg-white p-2 shadow-sm"
                >
                  <div
                    className="h-8 w-8 rounded-xl border border-black/10 shadow-sm"
                    style={{ backgroundColor: item.hex }}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-cocoa">{item.name}</p>
                    <p className="text-xs text-cocoa-light">× {item.count} · {percent}%</p>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
